import { createSlice } from "@reduxjs/toolkit";

import {
  IWorkOrder,
  TRelatedServiceTask,
  WorkOrderCostLimit,
} from "Shared/Types/workOrder";
import { WorkOrderRetrieve } from "Shared/Types/fleetmanagementapi";

import { RootState } from "../store";
import {
  addWorkOrder,
  addWorkOrderComment,
  addWorkOrderCostLimit,
  editWorkOrder,
  editWorkOrderCostLimit,
  getWorkOrder,
  getWorkOrderCostLimit,
  getWorkOrderList,
  getWorkOrderRelatedServiceTasks,
} from "./reducer";

interface ICompanyRequestsState {
  error?: string | null;
  loading: boolean;
  isFuncLoading: boolean;
  success?: boolean;
  loadingEdit: boolean;
  numPage: number;
  currentWorkOrder: IWorkOrder | null;
  relatedServiceTasks: TRelatedServiceTask | null;
  workOrderList: {
    error?: string | null;
    loading: boolean;
    success?: boolean | null;
    workOrderList: WorkOrderRetrieve[] | [];
  };
  workOrderCostLimit: {
    data: WorkOrderCostLimit[] | null;
    loading: boolean;
    functionLoading: boolean;
  };
}

const initialState: ICompanyRequestsState = {
  loading: false,
  isFuncLoading: false,
  error: null,
  loadingEdit: false,
  numPage: 0,
  currentWorkOrder: null,
  relatedServiceTasks: null,
  workOrderList: {
    loading: false,
    error: null,
    success: false,
    workOrderList: [],
  },
  workOrderCostLimit: {
    data: null,
    loading: false,
    functionLoading: false,
  },
};

const workOrderSlice = createSlice({
  name: "workOrder",
  initialState,
  reducers: {
    clear(state: any) {
      state.error = null;
      state.loading = false;
      state.success = false;
    },
    clearCurrentWorkOrder(state: any) {
      state.currentWorkOrder = null;
    },
  },
  extraReducers: (builder: any) => {
    builder.addCase(getWorkOrder.pending, (state: any) => {
      state.loading = true;
    });
    builder.addCase(getWorkOrder.rejected, (state: any) => {
      state.loading = false;
    });
    builder.addCase(getWorkOrder.fulfilled, (state: any, action: any) => {
      state.loading = false;
      state.error = null;
      state.currentWorkOrder = action.payload;
    });
    builder.addCase(
      getWorkOrderRelatedServiceTasks.fulfilled,
      (state: any, action: any) => {
        state.loading = false;
        state.error = null;
        state.relatedServiceTasks = action.payload;
      },
    );
    builder.addCase(getWorkOrderList.pending, (state: any, action: any) => {
      state.workOrderList.loading = true;
      state.workOrderList.error = null;
    });
    builder.addCase(getWorkOrderList.fulfilled, (state: any, action: any) => {
      state.workOrderList.loading = false;
      state.workOrderList.error = null;
      state.workOrderList.success = true;
      state.workOrderList.workOrderList = action.payload.results;
      state.numPage = action.payload.count;
    });
    builder.addCase(getWorkOrderList.rejected, (state: any, action: any) => {
      state.workOrderList.loading = false;
      state.workOrderList.error = action.error.message;
      state.workOrderList.success = false;
    });

    builder.addCase(editWorkOrder.pending, (state: any) => {
      state.isFuncLoading = true;
    });
    builder.addCase(editWorkOrder.fulfilled, (state: any) => {
      state.isFuncLoading = false;
    });
    builder.addCase(editWorkOrder.rejected, (state: any) => {
      state.isFuncLoading = false;
    });

    builder.addCase(addWorkOrder.pending, (state: any) => {
      state.isFuncLoading = true;
    });

    builder.addCase(addWorkOrder.fulfilled, (state: any) => {
      state.isFuncLoading = false;
    });
    builder.addCase(addWorkOrder.rejected, (state: any) => {
      state.isFuncLoading = false;
    });
    builder.addCase(addWorkOrderComment.pending, (state: any) => {
      state.isFuncLoading = true;
    });
    builder.addCase(addWorkOrderComment.fulfilled, (state: any) => {
      state.isFuncLoading = false;
    });
    builder.addCase(addWorkOrderComment.rejected, (state: any) => {
      state.isFuncLoading = false;
    });
    builder.addCase(getWorkOrderCostLimit.pending, (state: any) => {
      state.workOrderCostLimit.loading = true;
    });
    builder.addCase(getWorkOrderCostLimit.fulfilled, (state: any, action: any) => {
      state.workOrderCostLimit.loading = false;
      state.workOrderCostLimit.data = action.payload.results;
    });
    builder.addCase(getWorkOrderCostLimit.rejected, (state: any) => {
      state.workOrderCostLimit.loading = false;
    });
    builder.addCase(addWorkOrderCostLimit.pending, (state: any) => {
      state.workOrderCostLimit.functionLoading = true;
    });
    builder.addCase(addWorkOrderCostLimit.fulfilled, (state: any) => {
      state.workOrderCostLimit.functionLoading = false;
    });
    builder.addCase(addWorkOrderCostLimit.rejected, (state: any) => {
      state.workOrderCostLimit.functionLoading = false;
    });
    builder.addCase(editWorkOrderCostLimit.pending, (state: any) => {
      state.workOrderCostLimit.functionLoading = true;
    });
    builder.addCase(editWorkOrderCostLimit.fulfilled, (state: any) => {
      state.workOrderCostLimit.functionLoading = false;
    });
    builder.addCase(editWorkOrderCostLimit.rejected, (state: any) => {
      state.workOrderCostLimit.functionLoading = false;
    });
  },
});
export const { clear, clearCurrentWorkOrder } = workOrderSlice.actions;
export const selectCurrentWorkOrder = (state: RootState) =>
  state.work_order.currentWorkOrder;
export const selectWorkOrderIsFuncLoading = (state: RootState) =>
  state.work_order.isFuncLoading;
export const selectCurrentWorkOrderLoading = (state: RootState) =>
  state.work_order.loading;
export const selectWorkOrderRelatedServiceTasks = (state: RootState) =>
  state.work_order.relatedServiceTasks;
export const selectWorkOrderNumPage = (state: RootState) =>
  state.work_order.numPage;
export const selectWorkOrderList = (state: RootState) =>
  state.work_order.workOrderList.workOrderList;
export const selectWorkOrderListLoading = (state: RootState) =>
  state.work_order.workOrderList.loading;
export const selectWorkOrderListError = (state: RootState) =>
  state.work_order.workOrderList.error;
export const selectEditWorkOrderLoading = (state: RootState) =>
  state.work_order.loadingEdit;
export const selectWorkOrderListSuccess = (state: RootState) =>
  state.work_order.workOrderList.success;
export const selectWorkOrderCostLimit = (state: RootState) =>
  state.work_order.workOrderCostLimit.data;
export const selectWorkOrderCostLimitLoading = (state: RootState) =>
  state.work_order.workOrderCostLimit.loading;
export const selectWorkOrderCostLimitFunctionLoading = (state: RootState) =>
  state.work_order.workOrderCostLimit.functionLoading;

export default workOrderSlice;
